// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getCookie, setCookie } from "@insite/mobius/utilities/cookies";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CUSTOM_ELEMENTS, fontConfig } from "@vanmeter/XDL/constants";
import Close from "@vanmeter/XDL/Icons/IconClose";
import { color, getSystemColor, layoutContainer, xParserOptions } from "@vanmeter/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    message = "message",
    bgColor = "bgColor",
    dismissible = "dismissible",
    hideAfterClose = "hideAfterClose",
}

interface Props extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.bgColor]: string;
        [fields.dismissible]: boolean;
        [fields.hideAfterClose]: boolean;
    };
}

export interface GlobalNotificationBarStyles {
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    message?: TypographyProps;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconProps;
}

export const globalNotificationBarStyles: GlobalNotificationBarStyles = {
    wrapper: {
        css: css`
            padding: 8px 0;
            ${({ bgColor }: any) => bgColor && ` background-color: ${bgColor}`}
        `,
    },
    messageWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            ${layoutContainer}
        `,
    },
    message: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.bold,
        css: css`
            max-width: 90%;
            margin: 0 auto;

            ${TypographyStyle} {
                font-size: ${fontConfig.xSmallSingleLine.size}px;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                font-weight: ${fontConfig.weight.bold};
                text-align: center;
                margin-bottom: 0;
                letter-spacing: 0;
                word-break: break-word;
            }
        `,
    },
    closeClickable: {
        css: css`
            &:focus {
                outline: none;
            }
            &:hover > ${IconWrapper} {
                color: ${color("common.ui15")};
            }
        `,
    },
    closeIcon: {
        src: Close,
        width: 9,
        height: 13,
        color: "common.ui1",
    },
};

const styles = globalNotificationBarStyles;

const GlobalNotificationBar: React.FunctionComponent<Props> = (props: Props) => {
    const [visible, setVisibility] = React.useState(false);
    const {
        id,
        fields: { message, bgColor, dismissible, hideAfterClose },
    } = props;

    React.useEffect(() => {
        if (!hideAfterClose || (hideAfterClose && !getCookie(`GlobalMessage-${id}`))) {
            setVisibility(true);
        }
    }, []);

    if (!visible || !message) {
        return null;
    }

    const handleMessageClose = () => {
        setVisibility(false);
        setCookie(`GlobalMessage-${id}`, "true");
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ bgColor }} className="global-notification-wrapper">
            <StyledWrapper {...styles.messageWrapper}>
                <Typography {...styles.message}>{parse(message, xParserOptions)}</Typography>
                {dismissible && (
                    <Clickable
                        {...styles.closeClickable}
                        onClick={handleMessageClose}
                        data-test-selector="global_notification_message_close"
                        aria-label="Global Notification Message Close Clickable"
                    >
                        <Icon {...styles.closeIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalNotificationBar,
    definition: {
        group: CUSTOM_ELEMENTS,
        icon: "Info",
        fieldDefinitions: [
            {
                name: fields.message,
                displayName: "Message",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
                sortOrder: 1,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("danger"),
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
            },
            {
                name: fields.dismissible,
                displayName: "Dismissible",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.hideAfterClose,
                displayName: "Hide After Close",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
                tooltip: "Once the user close the message, it will not appear again during future visits to the site.",
                isVisible: item => item.fields.dismissible,
            },
        ],
        allowedContexts: ["Header", "CompactHeader"],
    },
};

export default widgetModule;
